/* IONZ — commerce layer.
 *
 * Replaces the demo's in-memory mock with the real Cart AJAX API. Two rules govern
 * this file:
 *
 *   1. Prices are never computed here. Every figure comes from Liquid, which read it
 *      from variant.selling_plan_allocations. The demo multiplied by 0.85 in JS; that
 *      silently lies the moment anyone edits the discount in the Subscriptions app.
 *   2. Money is never formatted here. After each mutation the cart drawer is
 *      re-rendered server-side via the Section Rendering API, so shop.money_format
 *      stays the single source of truth.
 */
(function () {
  'use strict';

  var CFG = window.IONZ || {};
  var ROUTES = CFG.routes || {};
  var STR = CFG.strings || {};
  var CART_ADD = ROUTES.cartAdd || '/cart/add';
  var CART_CHANGE = ROUTES.cartChange || '/cart/change';

  function $(sel, ctx) { return (ctx || document).querySelector(sel); }
  function $$(sel, ctx) { return Array.prototype.slice.call((ctx || document).querySelectorAll(sel)); }

  /* ---------------------------------------------------------------- toast */

  var toastTimer;
  function toast(msg) {
    var el = document.getElementById('toast');
    if (!el) return;
    el.textContent = msg;
    el.classList.add('show');
    clearTimeout(toastTimer);
    toastTimer = setTimeout(function () { el.classList.remove('show'); }, 2600);
  }

  /* ------------------------------------------------------------ cart drawer */

  var lastFocus = null;

  function drawer() { return $('[data-cart-drawer]'); }
  function scrim() { return document.getElementById('scrim'); }

  function openCart() {
    var d = drawer(), s = scrim();
    if (!d) return;
    lastFocus = document.activeElement;
    d.classList.add('open');
    if (s) { s.hidden = false; s.classList.add('open'); }
    document.body.style.overflow = 'hidden';
    var close = $('[data-cart-close]', d);
    if (close) close.focus();
  }

  function closeCart() {
    var d = drawer(), s = scrim();
    if (d) d.classList.remove('open');
    if (s) { s.classList.remove('open'); s.hidden = true; }
    document.body.style.overflow = '';
    if (lastFocus && lastFocus.focus) lastFocus.focus();
    lastFocus = null;
  }

  function isOpen() {
    var d = drawer();
    return !!d && d.classList.contains('open');
  }

  /* Swap in freshly rendered sections, preserving the drawer's open state —
     replaceWith drops the class otherwise. */
  function applySections(sections) {
    if (!sections) return;
    var wasOpen = isOpen();

    Object.keys(sections).forEach(function (name) {
      var id = 'shopify-section-' + name;
      var current = document.getElementById(id);
      if (!current) return;
      var doc = new DOMParser().parseFromString(sections[name], 'text/html');
      var fresh = doc.getElementById(id);
      if (fresh) current.replaceWith(fresh);
    });

    if (wasOpen) {
      var d = drawer();
      if (d) d.classList.add('open');
    }
  }

  /* Which sections to ask the Section Rendering API to re-render.
   *
   * Sections placed in a section group are not addressable by their file name:
   * Shopify gives them a generated id like "sections--22581937242212__header".
   * Requesting "header" silently returns nothing and the cart bubble never moves.
   * So the header is located through the element that actually carries the count
   * and its real id is read off the DOM — correct on any store, and it survives
   * the group being renamed or rebuilt. */
  function sectionsParam() {
    var names = ['cart-drawer'];
    var countEl = document.querySelector('[data-cart-count]');
    var wrapper = countEl && countEl.closest('[id^="shopify-section-"]');
    if (wrapper) names.push(wrapper.id.replace('shopify-section-', ''));
    return names.join(',');
  }

  function cartRequest(url, payload) {
    payload.sections = sectionsParam();
    payload.sections_url = window.location.pathname;
    return fetch(url + '.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(payload)
    }).then(function (res) {
      return res.json().then(function (data) {
        if (!res.ok) throw new Error(data.description || data.message || STR.error || 'Cart error');
        return data;
      });
    });
  }

  /* Delegated so the handlers survive the drawer being replaced wholesale. */
  document.addEventListener('click', function (e) {
    var close = e.target.closest('[data-cart-close]');
    if (close) { e.preventDefault(); closeCart(); return; }

    var open = e.target.closest('[data-cart-open]');
    if (open) { e.preventDefault(); openCart(); return; }

    var remove = e.target.closest('[data-cart-remove]');
    if (remove) {
      e.preventDefault();
      var line = parseInt(remove.getAttribute('data-cart-remove'), 10);
      remove.disabled = true;
      cartRequest(CART_CHANGE, { line: line, quantity: 0 })
        .then(function (data) { applySections(data.sections); })
        .catch(function (err) { toast(err.message); remove.disabled = false; });
    }
  });

  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape' && isOpen()) { closeCart(); return; }

    /* Keep Tab inside the drawer while it is modal. */
    if (e.key === 'Tab' && isOpen()) {
      var d = drawer();
      var focusable = $$('a[href],button:not([disabled]),input:not([disabled]),select:not([disabled]),textarea', d)
        .filter(function (el) { return el.offsetParent !== null; });
      if (!focusable.length) return;
      var first = focusable[0], last = focusable[focusable.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    }
  });

  /* ----------------------------------------------------------- product form */

  function initProduct(root) {
    var dataEl = $('[data-product-json]', root);
    var form = $('[data-product-form]', root);
    if (!dataEl || !form) return;

    var data;
    try { data = JSON.parse(dataEl.textContent); } catch (err) { return; }

    var variantInput = $('[data-variant-input]', form);
    var planInput = $('[data-plan-input]', form);
    var planSelect = $('[data-plan-select]', form);
    var freqRow = $('[data-freq-row]', form);
    var addButton = $('[data-add-button]', form);
    var addLabel = $('[data-add-label]', form);
    var mobileAdd = $('[data-mobile-add]', root);

    var state = {
      variant: data.variants.filter(function (v) { return String(v.id) === String(variantInput.value); })[0] || data.variants[0],
      mode: data.hasPlans ? (data.defaultMode || 'one') : 'one',
      planId: planSelect ? planSelect.value : null
    };

    function allocation() {
      if (state.mode !== 'sub' || !state.planId) return null;
      return state.variant.plans ? state.variant.plans[state.planId] : null;
    }

    function setText(role, text) {
      var el = $('[data-role="' + role + '"]', root);
      if (el) el.textContent = text;
    }

    function render() {
      var v = state.variant;
      var alloc = allocation();
      var nowText = alloc ? alloc.priceText : v.priceText;

      setText('oneprice', v.priceText);

      /* The subscribe tile always advertises the subscription price for the
         selected variant and frequency, even while one-time is active. */
      var subAlloc = (state.planId && v.plans) ? v.plans[state.planId] : null;
      setText('subprice', subAlloc ? subAlloc.priceText : v.priceText);

      setText('pricenow', nowText);
      setText('mobarprice', nowText);

      var old = $('[data-role="priceold"]', root);
      if (old) {
        var showOld = !!(alloc && alloc.price < v.price);
        old.hidden = !showOld;
        if (showOld) old.textContent = v.priceText;
      }

      var available = v.available;
      if (addButton) addButton.disabled = !available;
      if (mobileAdd) mobileAdd.disabled = !available;
      if (addLabel) addLabel.textContent = available ? (STR.addToCart || 'Add to cart') : (STR.soldOut || 'Sold out');

      variantInput.value = v.id;

      if (planInput) {
        if (state.mode === 'sub' && state.planId) {
          planInput.value = state.planId;
          planInput.disabled = false;
        } else {
          planInput.disabled = true;
        }
      }

      if (freqRow) freqRow.classList.toggle('on', state.mode === 'sub');
    }

    root.addEventListener('click', function (e) {
      var choice = e.target.closest('[data-variant-choice]');
      if (choice && !choice.disabled) {
        var id = choice.getAttribute('data-variant-choice');
        var next = data.variants.filter(function (v) { return String(v.id) === String(id); })[0];
        if (next) {
          state.variant = next;
          $$('[data-variant-choice]', root).forEach(function (b) { b.setAttribute('aria-pressed', 'false'); });
          choice.setAttribute('aria-pressed', 'true');
          render();
        }
        return;
      }

      var mode = e.target.closest('[data-mode]');
      if (mode) {
        state.mode = mode.getAttribute('data-mode');
        $$('[data-mode]', root).forEach(function (b) { b.setAttribute('aria-pressed', 'false'); });
        mode.setAttribute('aria-pressed', 'true');
        render();
        return;
      }

      if (e.target.closest('[data-mobile-add]')) {
        e.preventDefault();
        form.requestSubmit ? form.requestSubmit() : submit();
      }
    });

    if (planSelect) {
      planSelect.addEventListener('change', function () {
        state.planId = planSelect.value;
        render();
      });
    }

    function submit() {
      var item = { id: state.variant.id, quantity: 1 };
      if (state.mode === 'sub' && state.planId) item.selling_plan = Number(state.planId);

      if (addButton) addButton.disabled = true;
      if (mobileAdd) mobileAdd.disabled = true;

      cartRequest(CART_ADD, { items: [item] })
        .then(function (res) {
          applySections(res.sections);
          openCart();
          toast(STR.added || 'Added to cart');
        })
        .catch(function (err) { toast(err.message); })
        .then(function () { render(); });
    }

    form.addEventListener('submit', function (e) {
      e.preventDefault();
      submit();
    });

    /* Gallery — thumbnails swap the main image and its srcset together, so the
       browser does not keep serving the previous image's candidate set. */
    root.addEventListener('click', function (e) {
      var thumb = e.target.closest('[data-gallery-thumb]');
      if (!thumb) return;
      var main = $('[data-gallery-main]', root);
      if (!main) return;
      main.src = thumb.getAttribute('data-full');
      main.srcset = thumb.getAttribute('data-srcset') || '';
      main.alt = thumb.getAttribute('data-alt') || '';
      $$('[data-gallery-thumb]', root).forEach(function (b) { b.classList.remove('active'); });
      thumb.classList.add('active');
    });

    render();
  }

  function boot() {
    $$('[data-product-root]').forEach(initProduct);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();

  /* The theme editor re-renders sections on edit, which destroys inline state. */
  document.addEventListener('shopify:section:load', function (e) {
    var root = $('[data-product-root]', e.target);
    if (root) initProduct(root);
  });

  window.IONZ = window.IONZ || {};
  window.IONZ.openCart = openCart;
  window.IONZ.closeCart = closeCart;
  window.IONZ.toast = toast;
})();
